import { MenuItem, ProductChannel } from '@core/models';
import { MenuItemCharges } from '@core/models/menu-item/menu-item';

export function detectCheckoutPriceChanges(updatedData: MenuItem[], data: MenuItem[]): MenuItemCharges[] {
	const changes: MenuItemCharges[] = [];

	for (const cartItem of data) {
		const updatedItem = updatedData.find(item => item.idArticulo === cartItem.idArticulo);
		if (!updatedItem) continue;

		const cartItemPriceValue = cartItem?.precio ?? 0n;
		const cartPrices = getCheckoutProductPrices(cartItem);
		const updatedPrices = getCheckoutProductPrices(updatedItem);
		if (updatedPrices.some(price => price.precio === cartItemPriceValue)) continue;

		const changedPrice = findChangedCheckoutPrice(cartPrices, updatedPrices, cartItemPriceValue);
		if (!changedPrice) continue;

		changes.push({
			idArticulo: updatedItem.idArticulo,
			idPrecio: changedPrice.idPrecio,
			nombre: updatedItem.nombre,
			variacionNombre: changedPrice.nombre || null,
			precioAnterior: cartItemPriceValue,
			precioNuevo: changedPrice.precio,
		});
	}

	return changes;
}

export function updateCheckoutCartPrices(data: MenuItem[], updatedData: MenuItem[]): MenuItem[] {
	const updatedCart: MenuItem[] = [];

	for (const cartItem of data) {
		const updatedItem = updatedData.find(item => item.idArticulo === cartItem.idArticulo);
		if (!updatedItem) continue;

		const cartItemPriceValue = cartItem?.precio ?? 0n;
		const cartItemChannelProducts = cartItem?.productosCanal || [];
		const [cartItemFirstChannel] = cartItemChannelProducts || [];
		const cartItemProductChannelPrices = cartItemFirstChannel?.precios || [];
		const updatedChannelProducts = updatedItem?.productosCanal || [];
		const [updatedFirstChannel] = updatedChannelProducts || [];
		const updatedProductChannelPrices = updatedFirstChannel?.precios || [];
		if (updatedProductChannelPrices.some(price => price.precio === cartItemPriceValue)) continue;

		const updatedProductChannelPrice = findChangedCheckoutPrice(
			cartItemProductChannelPrices,
			updatedProductChannelPrices,
			cartItemPriceValue
		);
		if (!updatedProductChannelPrice) continue;

		const safeChannel = updatedFirstChannel ?? cartItemFirstChannel;
		if (!safeChannel) continue;

		updatedCart.push({
			...cartItem,
			precio: updatedProductChannelPrice.precio,
			precioBase: updatedProductChannelPrice.precioBase,
			specialPriceAmount: updatedProductChannelPrice.specialPriceAmount ?? undefined,
			productosCanal: [{ ...safeChannel, precios: updatedProductChannelPrices } as ProductChannel],
		});
	}

	return updatedCart;
}

function getCheckoutProductPrices(item: MenuItem): ProductChannel['precios'] {
	return item.productosCanal?.[0]?.precios ?? [];
}

function findChangedCheckoutPrice(
	cartPrices: ProductChannel['precios'],
	updatedPrices: ProductChannel['precios'],
	cartItemPrice: bigint
): ProductChannel['precios'][number] | null {
	const previousPrice = cartPrices.find(price => price.precio === cartItemPrice);
	if (!previousPrice || updatedPrices.length === 0) return null;

	return updatedPrices.find(price => price.idPrecio === previousPrice.idPrecio) ?? updatedPrices[0];
}

export function transformCheckoutStockResponses(
	responses: MenuItem[],
	cartItems: MenuItem[]
): MenuItem[] {
	const updatedStockItems = responses
		.filter((item): item is MenuItem => !!item && isValidCheckoutStockItem(item))
		.map(item => ({
			...item,
			stock: item.stock ? Math.floor(Number(item.stock)) : null,
		}));

	return cartItems.map(cartItem => {
		const matchingItem = updatedStockItems.find(item => item.idArticulo === cartItem.idArticulo);
		return { ...cartItem, stock: matchingItem ? matchingItem.stock : cartItem.stock };
	});
}

function isValidCheckoutStockItem(item: MenuItem): boolean {
	return (
		typeof item.idCartItem === 'string' &&
		item.idCartItem.trim().length > 0 &&
		typeof item.idArticulo === 'string' &&
		item.idArticulo.trim().length > 0 &&
		typeof item.nombre === 'string' &&
		item.precio !== undefined &&
		item.precio !== null &&
		item.cantidad !== undefined &&
		item.cantidad !== null &&
		typeof item.storeSlug === 'string'
	);
}
